import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  CalendarDaysIcon,
  FireIcon,
  CloudIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';
import { useAppData } from '../../contexts/AppDataContext';

const seasons = [
  { id: 'winter', name: 'Hiver', months: ['Déc', 'Jan', 'Fév'], icon: <CloudIcon className="h-5 w-5 text-white" /> },
  { id: 'spring', name: 'Printemps', months: ['Mar', 'Avr', 'Mai'], icon: <CalendarDaysIcon className="h-5 w-5 text-white" /> },
  { id: 'summer', name: 'Été', months: ['Juin', 'Juil', 'Août'], icon: <FireIcon className="h-5 w-5 text-white" /> },
  { id: 'fall', name: 'Automne', months: ['Sep', 'Oct', 'Nov'], icon: <ArrowTrendingUpIcon className="h-5 w-5 text-white" /> },
];

const SeasonalityAnalysis = () => {
  const navigate = useNavigate();
  const { products, sales } = useAppData();
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeMetric, setActiveMetric] = useState('sales');
  const [seasonalityData, setSeasonalityData] = useState([]);

  // Générer les données de saisonnalité basées sur les données réelles
  useEffect(() => {
    generateSeasonalityData();
  }, [products, sales]);

  const generateSeasonalityData = () => {
    try {
      setIsLoading(true);
      
      // Initialiser les données pour chaque mois
      const months = [
        'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
        'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'
      ];
      
      const initialData = months.map(month => ({
        month,
        sales: 0,
        orders: 0,
        avg: 0
      }));
      
      // Si nous avons des données de ventes réelles, les utiliser
      if (sales && sales.length > 0) {
        // Agréger les ventes par mois
        sales.forEach(sale => {
          try {
            // Extraire le mois de la date de vente
            const saleDate = new Date(sale.date);
            const monthIndex = saleDate.getMonth();
            const monthName = months[monthIndex];
            
            // Calculer le montant de la vente
            const saleAmount = parseFloat(sale.amount) || 0;
            
            // Mettre à jour les données pour ce mois
            initialData[monthIndex].sales += saleAmount;
            initialData[monthIndex].orders += 1;
          } catch (error) {
            console.error("Erreur lors du traitement d'une vente:", error);
          }
        });
      } else {
        // Générer des données simulées basées sur les produits disponibles
        const baseValue = products && products.length > 0 
          ? products.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0) / products.length * 10
          : 5000;
        
        // Appliquer des facteurs saisonniers aux données simulées
        const seasonalFactors = [0.7, 0.6, 0.9, 1.2, 1.3, 1.4, 1.6, 1.7, 1.5, 1.2, 1.0, 1.1];
        
        initialData.forEach((item, index) => {
          const factor = seasonalFactors[index];
          item.sales = Math.round(baseValue * factor * (0.9 + Math.random() * 0.2));
          item.orders = Math.round(item.sales / (baseValue * 0.15));
        });
      }
      
      // Calculer le panier moyen pour chaque mois
      initialData.forEach(item => {
        item.avg = item.orders > 0 ? Math.round(item.sales / item.orders) : 0;
      });
      
      setSeasonalityData(initialData);
    } catch (error) {
      console.error("Erreur lors de la génération des données de saisonnalité:", error);
      toast.error("Erreur lors de la génération des données de saisonnalité");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    generateSeasonalityData();
  };

  const filterDataBySeason = (seasonId) => {
    if (seasonId === 'all') return seasonalityData;
    
    const season = seasons.find(s => s.id === seasonId);
    if (!season) return seasonalityData;
    
    return seasonalityData.filter(item => season.months.includes(item.month));
  };

  const filteredData = filterDataBySeason(activeFilter);

  return (
    <div className="bg-gray-900/50 backdrop-blur-md p-6 rounded-2xl border border-white/10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => navigate('/advanced-analytics')} 
            className="p-2 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-white" />
          </button>
          <h2 className="text-xl font-semibold text-white">Analyse de Saisonnalité</h2>
        </div>
        
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => {}} 
            className="px-3 py-2 bg-white/5 rounded-xl hover:bg-white/10 transition-colors text-white text-sm flex items-center"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Exporter
          </button>
          
          <button 
            onClick={handleRefresh} 
            className={`p-2 bg-white/5 rounded-xl hover:bg-white/10 transition-colors ${isLoading ? 'animate-spin' : ''}`}
          >
            <ArrowPathIcon className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {seasons.map((season) => (
          <div 
            key={season.id}
            onClick={() => setActiveFilter(season.id)}
            className={`p-4 rounded-2xl ${activeFilter === season.id 
              ? 'bg-gradient-to-br from-red-500/30 to-red-700/30 border-2 border-red-500/50' 
              : 'bg-white/5 border border-white/10 hover:bg-white/10'} 
              cursor-pointer transition-all`}
          >
            <div className="flex items-center">
              <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-600">
                {season.id === 'winter' && <CloudIcon className="h-5 w-5 text-white" />}
                {season.id === 'spring' && <CalendarDaysIcon className="h-5 w-5 text-white" />}
                {season.id === 'summer' && <FireIcon className="h-5 w-5 text-white" />}
                {season.id === 'fall' && <ArrowTrendingUpIcon className="h-5 w-5 text-white" />}
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-white">{season.name}</h3>
                <p className="text-sm text-white/60">{season.months.join(', ')}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mb-6 flex space-x-2">
        <button
          onClick={() => setActiveFilter('all')}
          className={`px-4 py-2 rounded-xl text-sm font-medium 
            ${activeFilter === 'all' 
              ? 'bg-gradient-to-r from-red-500 to-red-700 text-white' 
              : 'bg-white/5 text-white hover:bg-white/10'}`}
        >
          Tous
        </button>
        
        <div className="relative">
          <button className="flex items-center px-4 py-2 bg-white/5 rounded-xl text-white text-sm hover:bg-white/10">
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filtres
          </button>
        </div>
      </div>
      
      <div className="bg-white/5 p-4 rounded-xl mb-6">
        <div className="flex space-x-4 mb-4">
          <button 
            onClick={() => setActiveMetric('sales')}
            className={`px-4 py-2 rounded-lg text-sm ${activeMetric === 'sales' ? 'bg-red-500 text-white' : 'bg-white/5 text-white'}`}
          >
            Ventes (DA)
          </button>
          <button 
            onClick={() => setActiveMetric('orders')}
            className={`px-4 py-2 rounded-lg text-sm ${activeMetric === 'orders' ? 'bg-red-500 text-white' : 'bg-white/5 text-white'}`}
          >
            Commandes
          </button>
          <button 
            onClick={() => setActiveMetric('avg')}
            className={`px-4 py-2 rounded-lg text-sm ${activeMetric === 'avg' ? 'bg-red-500 text-white' : 'bg-white/5 text-white'}`}
          >
            Panier moyen
          </button>
        </div>
        
        <motion.div
          key={activeMetric}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="h-80"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={filteredData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="month" stroke="rgba(255,255,255,0.6)" />
              <YAxis stroke="rgba(255,255,255,0.6)" />
              <Tooltip 
                contentStyle={{ backgroundColor: 'rgba(17,24,39,0.9)', borderColor: 'rgba(255,255,255,0.1)', color: '#fff' }}
                labelStyle={{ color: '#fff' }}
              />
              <Legend />
              <Bar 
                dataKey={activeMetric} 
                fill={activeMetric === 'sales' ? '#ef4444' : activeMetric === 'orders' ? '#3b82f6' : '#10b981'} 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>
      
      <div className="bg-white/5 p-6 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4">Insights de Saisonnalité</h3>
        
        <div className="space-y-4">
          {seasonalityData.length > 0 && (
            <>
              <div className="p-4 bg-gradient-to-r from-blue-500/20 to-blue-700/20 rounded-xl border border-blue-500/30">
                <h4 className="font-medium text-blue-300 mb-2">Tendances observées</h4>
                <p className="text-sm text-white/80">
                  {(() => {
                    // Trouver le mois avec les ventes les plus élevées
                    const maxSalesMonth = [...seasonalityData].sort((a, b) => b.sales - a.sales)[0];
                    // Trouver le mois avec les ventes les plus basses
                    const minSalesMonth = [...seasonalityData].sort((a, b) => a.sales - b.sales)[0];
                    
                    // Calculer l'augmentation en pourcentage
                    const increasePercentage = minSalesMonth.sales > 0 
                      ? Math.round((maxSalesMonth.sales - minSalesMonth.sales) / minSalesMonth.sales * 100) 
                      : 100;
                    
                    // Déterminer la saison du mois max
                    let maxSeason = 'l\'été';
                    for (const season of seasons) {
                      if (season.months.includes(maxSalesMonth.month)) {
                        maxSeason = season.name.toLowerCase();
                        break;
                      }
                    }
                    
                    return `Les ventes atteignent leur pic pendant ${maxSeason} (${maxSalesMonth.month}), avec une augmentation de ${increasePercentage}% par rapport aux mois les plus bas (${minSalesMonth.month}).`;
                  })()}
                </p>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-green-500/20 to-green-700/20 rounded-xl border border-green-500/30">
                <h4 className="font-medium text-green-300 mb-2">Recommandations</h4>
                <p className="text-sm text-white/80">
                  {(() => {
                    // Trouver les mois avec les ventes les plus basses
                    const lowSalesMonths = [...seasonalityData]
                      .sort((a, b) => a.sales - b.sales)
                      .slice(0, 2)
                      .map(item => item.month)
                      .join(' et ');
                    
                    return `Planifier les campagnes promotionnelles pour ${lowSalesMonths} pour stimuler les ventes pendant la période creuse.`;
                  })()}
                </p>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-yellow-500/20 to-yellow-700/20 rounded-xl border border-yellow-500/30">
                <h4 className="font-medium text-yellow-300 mb-2">Planification de stock</h4>
                <p className="text-sm text-white/80">
                  {(() => {
                    // Trouver le mois précédant le pic de ventes
                    const maxSalesMonth = [...seasonalityData].sort((a, b) => b.sales - a.sales)[0];
                    const maxSalesMonthIndex = seasonalityData.findIndex(item => item.month === maxSalesMonth.month);
                    const previousMonthIndex = (maxSalesMonthIndex - 1 + 12) % 12;
                    const previousMonth = seasonalityData[previousMonthIndex].month;
                    
                    return `Augmenter les niveaux de stock de 25% avant ${previousMonth} pour répondre à la demande accrue à venir.`;
                  })()}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeasonalityAnalysis; 